import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import { DropdownItem, Icon, TButtonColor, TIconName, theme } from '@treezor/design-system';

import { ActionSubMenu } from './ActionSubMenu';

export interface IActionMenuItemProps {
  className?: string;
  icon?: TIconName;
  label: string;
  subMenu?: React.JSX.Element[];
  color?: TButtonColor;
  onClick?: () => void;
  testId?: string;
}

export function ActionMenuItem({ className, icon, label, subMenu, color, onClick, testId }: IActionMenuItemProps) {
  const [subMenuPlacement, setSubMenuPlacement] = useState('right');
  const [shouldShowSubmenu, setShowSubMenu] = useState(false);

  const colorValue = !!color ? theme.button.secondary[color].active.text : null;
  const containerRef = useRef(null);
  const subMenuRef = useRef(null);

  useEffect(() => {
    if (shouldShowSubmenu && containerRef.current && subMenuRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const subMenuRect = subMenuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      const spaceOnRight = viewportWidth - containerRect.right;
      const spaceOnLeft = containerRect.left;

      if (spaceOnRight >= subMenuRect.width) {
        setSubMenuPlacement('right');
      } else if (spaceOnLeft >= subMenuRect.width) {
        setSubMenuPlacement('left');
      } else {
        setSubMenuPlacement(spaceOnRight > spaceOnLeft ? 'right' : 'left');
      }
    }
  }, [shouldShowSubmenu]);

  const handleSubMenu = useCallback(
    (ev: MouseEvent) => {
      if (ev.type == 'mouseenter') {
        setShowSubMenu(true);
      } else {
        setShowSubMenu(false);
      }
    },
    [setShowSubMenu],
  );

  if (!!subMenu) {
    return (
      <div className="relative w-full" ref={containerRef}>
        <div
          className="flex justify-between py-2 items-center"
          onMouseEnter={handleSubMenu}
          onMouseLeave={handleSubMenu}
        >
          {subMenuPlacement === 'left' && <Icon size="s" name="chevron_left" className="pl-4"></Icon>}
          <DropdownItem size="s" label={label} icon={icon} hasCustomMargin={true} color={colorValue} testId={testId} />
          {subMenuPlacement === 'right' && <Icon size="s" name="chevron_right" className="pr-4"></Icon>}
        </div>
        {shouldShowSubmenu && (
          <div
            ref={subMenuRef}
            className={`absolute  whitespace-nowrap top-0 ${subMenuPlacement === 'left' ? 'right-full' : 'left-full'}`}
            onMouseEnter={handleSubMenu}
            onMouseLeave={handleSubMenu}
          >
            <ActionSubMenu>{subMenu}</ActionSubMenu>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className={clsx('', className)}>
        <DropdownItem size="s" label={label} icon={icon} color={colorValue} onClick={onClick} testId={testId} />
      </div>
    );
  }
}
